import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, Trash2, AlertTriangle, ShieldCheck, MessageSquareWarning, XCircle } from "lucide-react";

// Iter 110 — Player-side contract confirmation with the ADMIN-REVIEW proof
// gate. Player must supply the casino account they registered under the
// streamer's code AND upload one or more proofs (same endpoint as
// onboarding: /players/me/proofs). Confirm is disabled until both are set.
// After submit the contract moves to admin_review; on proof_rejected the
// player waits for an admin to reopen.
//
// Iter 118 — Also surfaces the CONTRACT change-request loop (distinct from
// the dashboard offered-volume change-request in Iter 116). Max 3 rounds,
// enforced server-side at /contracts/{cid}/request-changes. On round 3 the
// backend closes the offer (endedReason=change_cutoff) — surfaced here as
// "offer closed". Internal credit handling (50% retention / refund math)
// is NEVER exposed to the player.

const ACCEPT_TYPES = "image/jpeg,image/png,application/pdf,video/mp4,video/quicktime,video/webm";
const MAX_CHANGE_ROUNDS = 3;

const bucketOf = (s) => {
  if (s === "pending_player_confirmation") return "confirm";
  if (s === "admin_review") return "review";
  if (s === "proof_rejected") return "rejected";
  if (s === "change_requested") return "awaiting_streamer";
  if (s === "finished") return "closed";  // rendered only when endedReason==="change_cutoff"
  return null;
};

export default function ContractIter3Confirm() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [doc, setDoc] = useState(null);
  const [openContract, setOpenContract] = useState(null);  // Iter 118 — keep the contract row so we know changeRoundCount
  const [accountName, setAccountName] = useState("");
  const [proofs, setProofs] = useState([]);          // [{id, path}]
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter 118 — Change-request panel state.
  const [crPanelOpen, setCrPanelOpen] = useState(false);
  const [crNote, setCrNote] = useState("");
  const [crBusy, setCrBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/contracts/mine");
      setRows((r.data || []).filter((c) => {
        if (c.flow !== "iter3") return false;
        const b = bucketOf(c.status);
        if (!b) return false;
        // Only surface the change-cutoff finished state, not other finished contracts.
        if (b === "closed" && c.endedReason !== "change_cutoff") return false;
        return true;
      }));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReview = async (cid) => {
    const c = rows.find((r) => r.id === cid);
    setOpenId(cid); setDoc(null); setOpenContract(c || null);
    setAccountName(""); setProofs([]);
    setCrPanelOpen(false); setCrNote("");
    try {
      const r = await api.get(`/contracts/${cid}/document`);
      setDoc(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const closeModal = () => {
    setOpenId(null); setDoc(null); setOpenContract(null);
    setAccountName(""); setProofs([]);
    setCrPanelOpen(false); setCrNote("");
  };

  const uploadProofs = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      setProofs((p) => [...p, ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const removeProof = async (proof) => {
    try {
      if (proof.path) await api.delete(`/players/me/proofs?path=${encodeURIComponent(proof.path)}`);
    } catch { /* best-effort remote delete */ }
    setProofs((p) => p.filter((x) => x !== proof));
  };

  const canSubmit = accountName.trim().length > 0 && proofs.length > 0 && !busy && !uploading;

  const submit = async () => {
    if (!openId || !canSubmit) return;
    setBusy(true);
    try {
      const proofFileIds = proofs.map((p) => p.id).filter(Boolean);
      await api.post(`/contracts/${openId}/confirm-iter3`, {
        accept: true,
        casinoAccountName: accountName.trim(),
        proofFileIds,
      });
      toast.success("Submitted — pending admin review");
      closeModal();
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  // Iter 118 — Submit a change request against the current pending contract.
  const roundsUsed  = Number(openContract?.changeRoundCount || 0);
  const roundsLeft  = Math.max(0, MAX_CHANGE_ROUNDS - roundsUsed);
  const nextRoundNo = roundsUsed + 1;
  const isFinalReq  = nextRoundNo >= MAX_CHANGE_ROUNDS;   // submitting this one triggers cutoff
  const canRequestChanges = roundsLeft > 0 && openContract?.status === "pending_player_confirmation";

  const submitChangeRequest = async () => {
    if (!openId || !crNote.trim() || crBusy) return;
    if (isFinalReq && !window.confirm("This is your FINAL change request. If no agreement is reached after this, the offer will be closed. Continue?")) return;
    setCrBusy(true);
    try {
      const { data } = await api.post(`/contracts/${openId}/request-changes`, { note: crNote.trim() });
      if (data?.cutoff) toast("Offer closed — three rounds reached", { icon: "•" });
      else              toast.success(`Changes requested — round ${data?.round || nextRoundNo} of ${MAX_CHANGE_ROUNDS}. Waiting for the streamer to revise.`);
      closeModal();
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "no_rounds_left") toast.error("No change rounds remaining");
      else toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setCrBusy(false); }
  };

  if (rows.length === 0) return null;

  return (
    <section className="lg:col-span-12 border border-[#F5B841]/40 p-6" data-testid="contract-iter3-confirm">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">Contracts awaiting your action</div>
      <div className="space-y-2">
        {rows.map((c) => {
          const b = bucketOf(c.status);
          return (
            <div key={c.id} className="flex items-center justify-between border border-white/10 p-3" data-testid={`iter3-row-${c.id}`}>
              <div className="text-xs font-mono">
                <div className="text-[#E8ECF4]">{c.vertical || "casino"} · {Number(c.agreed_monthly_volume || 0).toLocaleString()} USD/month · 90-day term</div>
                <div className="text-[10px] text-[#8B97AE]">id: {c.id?.slice(0, 8)} · <span className={b === "rejected" ? "text-[#F26D6D]" : b === "review" ? "text-[#2DD4A7]" : "text-[#F5B841]"}>{c.status.replace(/_/g, " ")}</span></div>
              </div>
              {b === "confirm" ? (
                <Button data-testid={`iter3-open-${c.id}`} onClick={() => openReview(c.id)} className="rounded-none bg-[#F5B841] text-black hover:bg-[#F5B841]/80 h-9 font-mono text-[11px] uppercase tracking-widest">Review &amp; Submit</Button>
              ) : b === "review" ? (
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]" data-testid={`iter3-state-review-${c.id}`}>Submitted · pending admin review</div>
              ) : b === "awaiting_streamer" ? (
                <div className="text-right space-y-0.5" data-testid={`iter3-state-changes-${c.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Changes requested · waiting for streamer to revise</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">Round {Number(c.changeRoundCount || 0)} of {MAX_CHANGE_ROUNDS}</div>
                </div>
              ) : b === "closed" ? (
                <div className="text-right space-y-0.5" data-testid={`iter3-state-closed-${c.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Offer closed</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">No agreement was reached after {MAX_CHANGE_ROUNDS} rounds.</div>
                </div>
              ) : (
                <div className="text-right space-y-0.5" data-testid={`iter3-state-rejected-${c.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Proof not accepted</div>
                  {c.proof_rejection_reason && <div className="text-[10px] text-[#E8ECF4] max-w-xs">{c.proof_rejection_reason}</div>}
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">Waiting for admin to reopen</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {openId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="w-full max-w-2xl bg-[#0B1A14] border border-[#F5B841]/40 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
              <div className="font-display font-bold text-lg text-[#E8ECF4]">{doc?.title || "Player Agreement"}</div>
              <button data-testid="iter3-close" onClick={closeModal} className="text-[#8B97AE] hover:text-white">✕</button>
            </div>
            {!doc ? <div className="p-6 text-xs text-[#8B97AE]">…</div> : (
              <div className="p-4 space-y-3 text-xs font-mono">
                <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Parties</span><div>{doc.party.alias} (player) ↔ {doc.counterparty.alias} (streamer)</div></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Vertical</span><div>{doc.vertical}</div></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Agreed wager · 3-month total</span><div className="text-[#2DD4A7]">{Number((doc.agreedMonthlyVolume || 0) * 3).toLocaleString()} USD</div></div>
                {Array.isArray(doc.wagerBreakdown) && doc.wagerBreakdown.length > 0 && (
                  <div className="border border-white/10 p-3" data-testid="iter3-wager-breakdown">
                    <div className="text-[10px] uppercase tracking-widest text-[#F5B841] mb-2">Agreed wager breakdown</div>
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-[11px] font-mono mb-2 pb-1 border-b border-white/10">
                      <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Category</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#8B97AE] text-right">Amount</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#8B97AE] text-right">Weighted (× coef)</span>
                    </div>
                    {doc.wagerBreakdown.map((row) => (
                      <div key={row.key} className="grid grid-cols-3 gap-x-2 text-[11px] font-mono py-0.5" data-testid={`iter3-wager-row-${row.key}`}>
                        <span className="text-[#E8ECF4]">{row.key.replace(/_/g, " ")}</span>
                        <span className="text-right tabular-nums">{Number(row.amount).toLocaleString()}</span>
                        <span className="text-right tabular-nums text-[#8B97AE]">{Number(row.weighted).toLocaleString()} <span className="text-[9px]">× {row.coefficient}</span></span>
                      </div>
                    ))}
                    <div className="grid grid-cols-3 gap-x-2 text-[11px] font-mono mt-2 pt-1 border-t border-white/10">
                      <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Weighted total</span>
                      <span></span>
                      <span className="text-right tabular-nums text-[#2DD4A7]" data-testid="iter3-wager-weighted-total">{Number(doc.wagerWeightedTotal || 0).toLocaleString()} USD</span>
                    </div>
                  </div>
                )}
                <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">70% floor</span><div className="text-[#F5B841]">{Number(doc.floorMinimum).toLocaleString()} USD</div></div>
                {typeof doc.rebateRatePct === "number" && (
                  <div className="border border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.05] p-3" data-testid="iter3-rebate-line">
                    <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Your cashback</span>
                    <div className="text-[#2DD4A7]">Rate {Math.round((doc.rebateRatePct || 0) * 100)}% · Expected cashback: {Number(doc.rebateExpectedAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</div>
                    <div className="text-[10px] text-[#8B97AE] mt-1" data-testid="iter3-rebate-scope">Whole-deal total for the 90-day term (not per month). Final amount scales with your verified delivered volume at settlement.</div>
                  </div>
                )}
                <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Settlement</span><div>{doc.settlement}</div></div>
                {doc.whereToRegister && (
                  <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3" data-testid="iter3-where-to-register">
                    <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Where to register</span>
                    <div className="text-[#F5B841] mt-0.5">
                      <div>Casino: <span className="text-[#E8ECF4]">{doc.whereToRegister.casino || "—"}</span></div>
                      <div>Affiliate code: <span className="text-[#E8ECF4]" data-testid="iter3-aff-code">{doc.whereToRegister.affiliateCode || "—"}</span></div>
                      {doc.whereToRegister.referralLink && (
                        <div>Referral link: <a data-testid="iter3-ref-link" href={doc.whereToRegister.referralLink} target="_blank" rel="noopener noreferrer" className="text-[#2DD4A7] underline break-all">{doc.whereToRegister.referralLink}</a></div>
                      )}
                    </div>
                    {doc.newAccountOnlyNote && (
                      <div className="text-[10px] text-[#8B97AE] mt-2" data-testid="iter3-new-account-note">{doc.newAccountOnlyNote}</div>
                    )}
                  </div>
                )}

                <div className="border border-white/10 p-3 space-y-3" data-testid="iter3-proof-section">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">
                    <ShieldCheck className="w-3 h-3" />Your casino account · admin review
                  </div>
                  <label className="block">
                    <div className="text-[11px] text-[#E8ECF4] mb-0.5">Casino account name <span className="text-[#F87171]">*</span></div>
                    <input
                      data-testid="iter3-account-name"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="Your username at the casino"
                      maxLength={120}
                      className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#2DD4A7]"
                    />
                  </label>
                  <div>
                    <div className="text-[11px] text-[#E8ECF4] mb-1">Upload proof(s) <span className="text-[#F87171]">*</span></div>
                    <input
                      data-testid="iter3-proof-input"
                      type="file"
                      multiple
                      accept={ACCEPT_TYPES}
                      onChange={(e) => uploadProofs(Array.from(e.target.files || []))}
                      className="block text-xs text-[#8B97AE] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-[#E8ECF4] file:px-3 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:tracking-widest hover:file:bg-white/10"
                    />
                    {uploading && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Uploading…</div>}
                    <ul className="mt-2 space-y-1" data-testid="iter3-proof-list">
                      {proofs.map((p, i) => (
                        <li key={p.id || i} className="flex items-center justify-between text-[11px] text-[#E8ECF4] border border-white/10 px-2 py-1">
                          <span className="font-mono truncate mr-2 flex items-center gap-1"><Upload className="w-3 h-3 text-[#2DD4A7]" />{p.filename || p.path || p.id}</span>
                          <button onClick={() => removeProof(p)} className="text-[#8B97AE] hover:text-[#F26D6D]" data-testid={`iter3-proof-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                        </li>
                      ))}
                      {proofs.length === 0 && (
                        <li className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] flex items-center gap-1"><AlertTriangle className="w-3 h-3" />At least one proof required</li>
                      )}
                    </ul>
                  </div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                    Your account name + proof go to admin review only. The streamer never sees them.
                  </p>
                </div>

                <div className="pt-3 border-t border-white/10 space-y-3">
                  {/* Iter 118 — Round indicator (info only). */}
                  {openContract && (
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest" data-testid="iter3-rounds-indicator">
                      <span className="text-[#8B97AE]">Change rounds</span>
                      <span className={roundsLeft === 0 ? "text-[#F26D6D]" : roundsLeft === 1 ? "text-[#F5B841]" : "text-[#8B97AE]"}>
                        Round {roundsUsed} used · {roundsLeft} of {MAX_CHANGE_ROUNDS} left
                      </span>
                    </div>
                  )}

                  <Button
                    data-testid="iter3-confirm-btn"
                    onClick={submit}
                    disabled={!canSubmit}
                    className="w-full rounded-none bg-[#2DD4A7] text-black hover:bg-[#F5B841] h-10 font-mono text-xs uppercase tracking-widest disabled:opacity-40"
                  >{busy ? "Submitting…" : "Submit for admin review"}</Button>

                  {/* Iter 118 — Request-changes alternative to accepting. */}
                  {canRequestChanges && !crPanelOpen && (
                    <button
                      data-testid="iter3-request-changes-open"
                      onClick={() => setCrPanelOpen(true)}
                      className="w-full inline-flex items-center justify-center gap-1.5 border border-[#F5B841]/40 text-[#F5B841] hover:bg-[#F5B841]/[0.08] h-10 font-mono text-xs uppercase tracking-widest"
                    >
                      <MessageSquareWarning className="w-3 h-3" />Request changes instead
                    </button>
                  )}

                  {canRequestChanges && crPanelOpen && (
                    <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.05] p-3 space-y-3" data-testid="iter3-cr-panel">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Request changes</div>
                        <button data-testid="iter3-cr-close" onClick={() => { setCrPanelOpen(false); setCrNote(""); }} className="text-[#8B97AE] hover:text-white"><XCircle className="w-3 h-3" /></button>
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="iter3-cr-rounds">
                        Round {nextRoundNo} of {MAX_CHANGE_ROUNDS} — you have {roundsLeft} change request{roundsLeft === 1 ? "" : "s"} left before this offer is finalized.
                      </div>
                      {isFinalReq && (
                        <div className="flex items-start gap-1.5 text-[10px] font-mono text-[#F26D6D]" data-testid="iter3-cr-final-warn">
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="uppercase tracking-widest">Final change request — if no agreement is reached after this, the offer will be closed.</span>
                        </div>
                      )}
                      <textarea
                        data-testid="iter3-cr-note"
                        rows={3}
                        maxLength={1000}
                        value={crNote}
                        onChange={(e) => setCrNote(e.target.value)}
                        placeholder="What would you like changed? (agreed wager, categories, cashback discussion, etc.)"
                        className="w-full bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#F5B841] resize-none"
                      />
                      <Button
                        data-testid="iter3-cr-submit"
                        onClick={submitChangeRequest}
                        disabled={!crNote.trim() || crBusy}
                        className={`w-full rounded-none h-10 font-mono text-xs uppercase tracking-widest disabled:opacity-40 ${isFinalReq ? "bg-[#F26D6D] hover:bg-[#F26D6D]/80 text-white" : "bg-[#F5B841] hover:bg-[#F5B841]/80 text-black"}`}
                      >{crBusy ? "Sending…" : isFinalReq ? "Send final request" : "Send change request"}</Button>
                    </div>
                  )}

                  {!canRequestChanges && roundsUsed >= MAX_CHANGE_ROUNDS && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] text-center border border-[#F26D6D]/30 p-2" data-testid="iter3-cr-exhausted">
                      Change rounds exhausted — accept or the offer will close.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
